/** Observations repeatable field key used across Compliance Audit apps */
export const OBSERVATIONS_KEY = '2d24';

/**
 * Optional overrides when schema walking is ambiguous (form_id -> parent repeatable key).
 * @type {Record<string, { parentRepeatableKey: string }>}
 */
const NESTED_FORM_OVERRIDES = {};

/**
 * @param {object|null|undefined} formData - Raw Fulcrum form from API (`form.data` from client.forms.find)
 * @param {string} [formId]
 * @returns {{ isNested: boolean, parentRepeatableKey: string|null }}
 */
export function getObservationsLayout(formData, formId) {
  if (formId && NESTED_FORM_OVERRIDES[formId]) {
    return {
      isNested: true,
      parentRepeatableKey: NESTED_FORM_OVERRIDES[formId].parentRepeatableKey,
    };
  }
  if (!formData?.elements) {
    return { isNested: false, parentRepeatableKey: null };
  }
  const topLevel = formData.elements.find((e) => e.key === OBSERVATIONS_KEY);
  if (topLevel) {
    return { isNested: false, parentRepeatableKey: null };
  }
  const parentKey = findRepeatableAncestorKeyForField(formData.elements, OBSERVATIONS_KEY, null);
  if (parentKey) {
    return { isNested: true, parentRepeatableKey: parentKey };
  }
  return { isNested: false, parentRepeatableKey: null };
}

/**
 * While walking the tree, `repeatableAncestorKey` is the innermost Repeatable section key
 * that contains the current subtree.
 */
function findRepeatableAncestorKeyForField(elements, targetKey, repeatableAncestorKey) {
  for (const el of elements || []) {
    const nextAncestor = el.type === 'Repeatable' ? el.key : repeatableAncestorKey;
    if (el.key === targetKey) {
      return repeatableAncestorKey;
    }
    const found = findRepeatableAncestorKeyForField(el.elements || [], targetKey, nextAncestor);
    if (found !== null && found !== undefined) {
      return found;
    }
  }
  return null;
}

/**
 * The Repeatable element for observations (top-level or nested under site visits).
 * @param {object|null|undefined} formData
 * @returns {object|null}
 */
export function findObservationsRepeatableElement(formData) {
  if (!formData?.elements) {
    return null;
  }
  const top = formData.elements.find((e) => e.key === OBSERVATIONS_KEY);
  if (top) {
    return top;
  }
  return findElementByKeyDeep(formData.elements, OBSERVATIONS_KEY);
}

function findElementByKeyDeep(elements, targetKey) {
  for (const el of elements || []) {
    if (el.key === targetKey) {
      return el;
    }
    const found = findElementByKeyDeep(el.elements || [], targetKey);
    if (found) {
      return found;
    }
  }
  return null;
}
